import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { Center, Grid, Stack } from '@mantine/core';
import { Legend } from 'components/Charts/Legend';
import { Orientation } from 'models/charts';
import { t } from 'i18next';
import { useDonutChartOptions, DonutChartOptionsProps } from './useDonutChartOptions';

export const DonutChart = <I,>(props: DonutChartOptionsProps<I>) => {
  const { data, onDistributionClick, layout = Orientation.Horizontal, labelPrefix } = props;
  const chartOptions = useDonutChartOptions(props);

  const calculateMeasurementPercentage = (value?: number) => {
    if (!value) return '';
    if (data) return `(${((value / data.total) * 100).toFixed(2)}%)`;
    return '-';
  };

  const showLabelPrefix = () => {
    if (labelPrefix) return labelPrefix;
    return '';
  };

  const showLabel = (name: string, value?: number) => {
    return `${t(`${showLabelPrefix()}${name}`)} - ${value} ${calculateMeasurementPercentage(value)}`;
  };

  const chartCallback = (chart: Highcharts.Chart) => {
    // Runs once when the chart is first created
    const subtitleEl = chart?.subtitle?.element;
    if (subtitleEl) {
      subtitleEl.setAttribute('data-testid', 'donut-chart-subtitle');
    }
  };

  const donut = <HighchartsReact highcharts={Highcharts} options={chartOptions} callback={chartCallback} />;

  const legend = (
    <Legend
      layout={Orientation.Vertical}
      items={data?.values.map((value) => {
        return {
          color: value.color,
          title: showLabel(value.label as string, value.total),
          icon: value.Icon,
          onClick: onDistributionClick,
          label: value.label as string,
        };
      })}
    />
  );

  if (layout === Orientation.Vertical) {
    return (
      <Stack align={'strech'}>
        {donut}
        <Center>{legend}</Center>
      </Stack>
    );
  }

  return (
    <Grid align={'center'}>
      <Grid.Col span={5}>{donut}</Grid.Col>
      <Grid.Col span={7}>{legend}</Grid.Col>
    </Grid>
  );
};
